import { useEffect, useMemo } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Placeholder } from "@tiptap/extensions";
import styled from "styled-components";
import FreeTextToolbar from "./FreeTextToolbar";
import { freeTextContentCss } from "./freeTextStyle";
import { createFreeTextExtensions, normalizePastedHtml } from "./freeTextConfig";

interface Props {
  postId: number;
  // 처음 불러올 HTML (수정 시 저장된 본문)
  initialHtml?: string | null;
  onChange: (html: string) => void;
  placeholder?: string;
}

// FreeText 본문 작성 편집기 (TipTap)
// 블록 단위가 아니라 문서 하나로 편집하므로 여러 문단 선택, 서식 유지 붙여넣기가 된다.
const FreeTextEditor = ({ postId, initialHtml, onChange, placeholder }: Props) => {
  const extensions = useMemo(
    () => [...createFreeTextExtensions(), Placeholder.configure({ placeholder: placeholder ?? "내용을 입력하세요." })],
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: initialHtml || "",
    immediatelyRender: true,
    editorProps: {
      transformPastedHTML: normalizePastedHtml,
    },
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  // 처음 내용도 부모가 알 수 있게 한 번 전달
  useEffect(() => {
    if (editor) onChange(editor.getHTML());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  return (
    <Wrapper>
      <FreeTextToolbar editor={editor} postId={postId} />
      <EditorContent editor={editor} className="freetext-editor" />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .freetext-editor .ProseMirror {
    min-height: 300px;
    padding: 1rem 1.25rem;
    outline: none;
    ${freeTextContentCss}
  }

  .freetext-editor .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    color: #bfbfbf;
    pointer-events: none;
  }

  /* 표 편집 중 선택한 칸 표시 */
  .freetext-editor .ProseMirror .selectedCell::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(22, 119, 255, 0.12);
    pointer-events: none;
  }
  .freetext-editor .ProseMirror td,
  .freetext-editor .ProseMirror th {
    position: relative;
  }

  .freetext-editor .ProseMirror img.ProseMirror-selectednode {
    outline: 2px solid #1677ff;
  }
`;

export default FreeTextEditor;
